import React, { useState, useEffect, useMemo } from 'react';
import { API_URL } from '../config';
import DashboardLayout from '../DashboardLayout/DasboardLayout';
import { 
  Users, 
  TrendingUp, 
  DollarSign, 
  Calendar, 
  Target, 
  Clock,
  Heart,
  Star,
  Award,
  Activity,
  Download,
  ChevronDown,
  ChevronUp,
  Trophy,
  Medal,
  Zap,
  BarChart3,
  PieChart,
  AlertCircle,
  Car
} from "lucide-react";

// Collapsible Tooltip Component
const ReportTooltip = ({ title, children }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="flex items-center justify-between w-full text-left"
      >
        <div className="flex items-center space-x-2">
          <AlertCircle className="text-blue-500" size={20} />
          <h3 className="text-lg font-semibold text-blue-900">{title}</h3>
        </div>
        {isExpanded ? <ChevronUp className="text-blue-500" size={20} /> : <ChevronDown className="text-blue-500" size={20} />}
      </button>
      {isExpanded && (
        <div className="mt-4 text-sm text-blue-800 space-y-2">
          {children}
        </div>
      )}
    </div>
  );
};

const CustomerPerformanceReport = () => {
  const [customers, setCustomers] = useState([]);
  const [trips, setTrips] = useState([]);
  const [rideshareTrips, setRideshareTrips] = useState([]);
  const [rideshareRequests, setRideshareRequests] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [customerPerformanceData, setCustomerPerformanceData] = useState([]); // Analytics data
  const [loading, setLoading] = useState(true);
  const [timeFilter, setTimeFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState(''); // Add missing searchTerm
  const [sortBy, setSortBy] = useState('totalSpent'); // Add missing sortBy
  const [sortOrder, setSortOrder] = useState('desc'); // Add missing sortOrder
  const [expandedSections, setExpandedSections] = useState({
    topPerformers: true,
    customerSegments: true,
    revenueAnalysis: true,
    activityAnalysis: true
  });

  // Filter trips based on date range
  const filterTripsByDate = (trips, dateRange) => {
    if (!dateRange) return trips;
    
    return trips.filter(trip => {
      if (!trip.order_start_datetime) return false;
      const tripDate = new Date(trip.order_start_datetime);
      return tripDate >= dateRange.start && tripDate <= dateRange.end;
    });
  };

  // Get date range based on filter
  const getDateRange = (filter) => {
    const now = new Date();
    const startOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    
    switch(filter) {
      case 'today':
        return {
          start: startOfDay,
          end: new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1)
        };
      case '7days':
        return {
          start: new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000),
          end: now
        };
      case '14days':
        return {
          start: new Date(now.getTime() - 14 * 24 * 60 * 60 * 1000),
          end: now
        };
      case '30days':
        return {
          start: new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000),
          end: now
        };
      default:
        return null;
    }
  };

  // Fetch all data with new customer analytics
  const fetchData = async () => {
    setLoading(true);
    try {
      const [customersRes, tripsRes, driversRes, customerPerformanceRes, rideshareRequestsRes, rideshareTripsRes] = await Promise.all([
        fetch(`${API_URL}/customerdetails/`),     // Customers (working)
        fetch(`${API_URL}/trip/`),                 // Private trips (working)
        fetch(`${API_URL}/driver/`),               // Drivers (working)
        fetch(`${API_URL}/commission/analytics/customers-performance?days=${timeFilter === 'all' ? 365 : timeFilter === '30days' ? 30 : timeFilter === '7days' ? 7 : 1}`), // Working endpoint
        fetch(`${API_URL}/rideshare/requests/all`), // Rideshare fares (per accepted passenger request)
        fetch(`${API_URL}/rideshare/trips`)        // Rideshare routes
      ]);

      const [customersData, tripsData, driversData, customerPerformanceData, rideshareRequestsData, rideshareTripsData] = await Promise.all([
        customersRes.json(),
        tripsRes.json(),
        driversRes.json(),
        customerPerformanceRes.json(),
        rideshareRequestsRes.json(),
        rideshareTripsRes.json()
      ]);

      setCustomers(customersData || []);
      setTrips(tripsData || []);                 // Private rides from trip
      setRideshareTrips(Array.isArray(rideshareTripsData) ? rideshareTripsData : []);
      setRideshareRequests(Array.isArray(rideshareRequestsData) ? rideshareRequestsData : []);
      setDrivers(driversData || []);

      // Set customer performance data from analytics endpoint with correct field mapping
      if (customerPerformanceData && customerPerformanceData.customers) {
        // Map backend field names to frontend expectations
        const mappedData = customerPerformanceData.customers.map(customer => ({
          ...customer,
          totalSpent: customer.total_spent || 0,
          privateRevenue: customer.private_rides_cost || 0,
          sharedRevenue: customer.shared_rides_cost || 0,
          totalRides: customer.total_rides_count || 0,
          privateTrips: customer.private_rides_count || 0,
          sharedTrips: customer.shared_rides_count || 0,
          avgCostPerRide: customer.average_cost_per_ride || 0
        }));
        setCustomerPerformanceData(mappedData);
      } else {
        // No analytics data — the memo below falls back to a manual calculation from
        // raw private trips + rideshare requests, so make sure it isn't left stale.
        setCustomerPerformanceData([]);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, [timeFilter]); // Add timeFilter dependency

  // Calculate customer performance metrics using analytics data
  const customerPerformance = useMemo(() => {
    // If we have analytics data, use it directly
    if (customerPerformanceData && customerPerformanceData.length > 0) {
      // Calculate totals from analytics data
      const totalCustomers = customerPerformanceData.length;
      const activeCustomers = customerPerformanceData.filter(c => c.totalRides > 0).length;
      const loyalCustomers = customerPerformanceData.filter(c => c.totalRides >= 5).length;
      const highPerformers = customerPerformanceData.filter(c => c.totalSpent > 100).length;

      const totalRevenue = customerPerformanceData.reduce((sum, c) => sum + (c.totalSpent || 0), 0);
      const avgCompletionRate = customerPerformanceData.length > 0 ?
        customerPerformanceData.reduce((sum, c) => sum + (c.completionRate || 0), 0) / customerPerformanceData.length : 0;
      const avgCustomerValue = totalCustomers > 0 ? totalRevenue / totalCustomers : 0;

      return {
        customers: customerPerformanceData,
        totalCustomers,
        activeCustomers,
        loyalCustomers,
        highPerformers,
        avgCompletionRate,
        avgCustomerValue,
        // These field names must match the mapping done in fetchData (`privateTrips`/`sharedTrips`),
        // not `totalPrivateRides`/`totalSharedRides` — those don't exist on the mapped objects and
        // silently summed to 0, hiding both private and rideshare trip counts.
        totalPrivateTrips: customerPerformanceData.reduce((sum, c) => sum + (c.privateTrips || 0), 0),
        totalRideshareTrips: customerPerformanceData.reduce((sum, c) => sum + (c.sharedTrips || 0), 0),
        totalPrivateRevenue: customerPerformanceData.reduce((sum, c) => sum + (c.privateRevenue || 0), 0),
        totalRideshareRevenue: customerPerformanceData.reduce((sum, c) => sum + (c.sharedRevenue || 0), 0)
      };
    }

    // Fallback to a manual calculation if the analytics endpoint returned nothing: derive the
    // same per-customer shape directly from private trips (`/trip`, matched on cust_id) and
    // accepted rideshare requests (`/rideshare/requests/all`, matched on passenger_id), so the
    // report doesn't silently collapse to all-zero rideshare (or private) totals.
    const manualCustomers = customers.map(customer => {
      const customerTrips = trips.filter(trip => trip.cust_id === customer.customerid);
      const customerRideshareRequests = rideshareRequests.filter(
        req => req.passenger_id === customer.customerid
      );

      const privateRevenue = customerTrips.reduce((sum, trip) =>
        sum + (parseFloat(trip.delivery_cost_proposed) || 0), 0
      );
      const sharedRevenue = customerRideshareRequests.reduce((sum, req) =>
        sum + (parseFloat(req.accepted_amount) || parseFloat(req.offer_amount) || 0), 0
      );
      const privateTrips = customerTrips.length;
      const sharedTrips = customerRideshareRequests.length;
      const totalRides = privateTrips + sharedTrips;
      const totalSpent = privateRevenue + sharedRevenue;

      return {
        ...customer,
        totalSpent,
        privateRevenue,
        sharedRevenue,
        totalRides,
        privateTrips,
        sharedTrips,
        avgCostPerRide: totalRides > 0 ? totalSpent / totalRides : 0,
        completionRate: 0 // Not derivable without the analytics endpoint's status breakdown
      };
    });

    const totalCustomers = manualCustomers.length;
    const activeCustomers = manualCustomers.filter(c => c.totalRides > 0).length;
    const loyalCustomers = manualCustomers.filter(c => c.totalRides >= 5).length;
    const highPerformers = manualCustomers.filter(c => c.totalSpent > 100).length;
    const totalRevenue = manualCustomers.reduce((sum, c) => sum + c.totalSpent, 0);
    const avgCustomerValue = totalCustomers > 0 ? totalRevenue / totalCustomers : 0;

    return {
      customers: manualCustomers,
      totalCustomers,
      activeCustomers,
      loyalCustomers,
      highPerformers,
      avgCompletionRate: 0,
      avgCustomerValue,
      totalPrivateTrips: manualCustomers.reduce((sum, c) => sum + c.privateTrips, 0),
      totalRideshareTrips: manualCustomers.reduce((sum, c) => sum + c.sharedTrips, 0),
      totalPrivateRevenue: manualCustomers.reduce((sum, c) => sum + c.privateRevenue, 0),
      totalRideshareRevenue: manualCustomers.reduce((sum, c) => sum + c.sharedRevenue, 0)
    };
  }, [customerPerformanceData, customers, trips, rideshareRequests]);

  // Filter customers based on search term
  const filteredCustomers = useMemo(() => {
    if (!customerPerformance || !customerPerformance.customers) return [];
    
    return customerPerformance.customers.filter(customer => {
      const searchLower = searchTerm.toLowerCase();
      return (
        customer.name?.toLowerCase().includes(searchLower) ||
        customer.customerid?.toLowerCase().includes(searchLower) ||
        customer.email?.toLowerCase().includes(searchLower)
      );
    });
  }, [customerPerformance, searchTerm]);

  // Sort customers
  const sortedCustomers = useMemo(() => {
    if (!filteredCustomers || filteredCustomers.length === 0) return [];
    
    return [...filteredCustomers].sort((a, b) => {
      let aValue, bValue;
      
      switch (sortBy) {
        case 'totalSpent':
          aValue = a.totalSpent || 0;
          bValue = b.totalSpent || 0;
          break;
        case 'totalRides':
          aValue = a.totalRides || 0;
          bValue = b.totalRides || 0;
          break;
        case 'completionRate':
          aValue = a.completionRate || 0;
          bValue = b.completionRate || 0;
          break;
        case 'avgRating':
          aValue = a.avgRating || 0;
          bValue = b.avgRating || 0;
          break;
        default:
          aValue = a.totalSpent || 0;
          bValue = b.totalSpent || 0;
      }
      
      if (sortOrder === 'desc') {
        return bValue - aValue;
      } else {
        return aValue - bValue;
      }
    });
  }, [filteredCustomers, sortBy, sortOrder]);

  // Get top customers by revenue
  const topCustomersByRevenue = useMemo(() => {
    if (!sortedCustomers || sortedCustomers.length === 0) return [];
    return sortedCustomers.slice(0, 5);
  }, [sortedCustomers]);

  // Get inactive customers
  const inactiveCustomers = useMemo(() => {
    if (!customerPerformance || !customerPerformance.customers) return [];
    return customerPerformance.customers.filter(customer => 
      (customer.totalRides || 0) === 0
    );
  }, [customerPerformance]);

  // Get filter label
  const getFilterLabel = (filter) => {
    switch(filter) {
      case 'today': return 'Today';
      case '7days': return 'Last 7 Days';
      case '14days': return 'Last 14 Days';
      case '30days': return 'Last 30 Days';
      default: return 'All Time';
    }
  };

  // Toggle section expansion
  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  // Customer Table Component
  const CustomerTable = ({ data, title }) => (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{title}</h3>
        <span className="text-sm text-gray-500 dark:text-gray-400">{data.length} customers</span>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm text-left">
          <thead className="text-xs text-gray-700 uppercase bg-gray-50 dark:bg-gray-700 dark:text-gray-400">
            <tr>
              <th className="px-4 py-3">Customer ID</th>
              <th className="px-4 py-3">Name</th>
              <th className="px-4 py-3">Private Trips</th>
              <th className="px-4 py-3">Rideshare Trips</th>
              <th className="px-4 py-3">Total Spent</th>
              <th className="px-4 py-3">Rating Received</th>
              <th className="px-4 py-3">Completion Rate</th>
            </tr>
          </thead>
          <tbody>
            {data.slice(0, 5).map((customer) => (
              <tr key={customer.customerid} className="border-b dark:border-gray-700">
                <td className="px-4 py-3 font-medium">{customer.customerid}</td>
                <td className="px-4 py-3">{customer.name || 'N/A'}</td>
                <td className="px-4 py-3">
                  <div className="text-blue-600 font-medium">{customer.private_completed_trips || 0}</div>
                  <div className="text-xs text-gray-500">+ {(customer.private_trip_ended_trips || 0)} ended</div>
                </td>
                <td className="px-4 py-3">
                  <div className="text-purple-600 font-medium">{customer.rideshare_completed_requests || 0}</div>
                  <div className="text-xs text-gray-500">+ {(customer.rideshare_cancelled_requests || 0)} cancelled</div>
                </td>
                <td className="px-4 py-3">
                  <div className="font-medium">${((customer.private_total_spend || 0) + (customer.rideshare_total_spend || 0)).toFixed(2)}</div>
                  <div className="text-xs text-gray-500">P: ${(customer.private_total_spend || 0).toFixed(2)} | R: ${(customer.rideshare_total_spend || 0).toFixed(2)}</div>
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center">
                    <span className="text-yellow-500">⭐</span>
                    <span className="ml-1">{(customer.private_average_rating_received || 0).toFixed(1)}</span>
                    <div className="text-xs text-gray-500">({customer.private_total_ratings_received || 0} ratings)</div>
                  </div>
                </td>
                <td className="px-4 py-3">{(customer.completionRate || 0).toFixed(1)}%</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  // Get top customers by performance
  const topCustomersByPerformance = useMemo(() => {
    if (!sortedCustomers || sortedCustomers.length === 0) return [];
    return sortedCustomers
      .filter(c => c.totalRides > 0)
      .sort((a, b) => (b.totalSpent || 0) - (a.totalSpent || 0))
      .slice(0, 5);
  }, [sortedCustomers]);

  // Get top customers by trips
  const topCustomersByTrips = useMemo(() => {
    if (!sortedCustomers || sortedCustomers.length === 0) return [];
    return sortedCustomers
      .filter(c => c.totalRides > 0)
      .sort((a, b) => (b.totalRides || 0) - (a.totalRides || 0))
      .slice(0, 5);
  }, [sortedCustomers]);

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2 flex items-center">
            <Users className="mr-3 text-blue-600" />
            Customer Performance Report
          </h1>
          <p className="text-gray-600 dark:text-gray-300">
            Comprehensive analysis of customer performance, revenue contribution, and engagement metrics
          </p>
        </div>

        {/* Time Filter */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-4">
          <div className="flex flex-wrap items-center justify-between">
            <div className="flex items-center space-x-2 mb-2 sm:mb-0">
              <Calendar className="text-blue-600" size={20} />
              <span className="font-medium text-gray-700 dark:text-gray-300">Time Period:</span>
            </div>
            <div className="flex flex-wrap gap-2">
              {[
                { value: "all", label: "All Time" },
                { value: "today", label: "Today" },
                { value: "7days", label: "Past 7 Days" },
                { value: "14days", label: "Past 14 Days" },
                { value: "30days", label: "Past 30 Days" }
              ].map(filter => (
                <button
                  key={filter.value}
                  onClick={() => setTimeFilter(filter.value)}
                  className={`px-4 py-2 rounded-lg font-medium transition-all ${
                    timeFilter === filter.value
                      ? "bg-blue-600 text-white shadow-lg transform scale-105"
                      : "bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600"
                  }`}
                >
                  {filter.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Enhanced Summary Cards with New Analytics */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
          <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Active Customers</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">
                  {customerPerformance.activeCustomers}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  of {customerPerformance.totalCustomers} total
                </p>
              </div>
              <Users className="text-blue-500" size={24} />
            </div>
          </div>
          
          <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Total Revenue</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">
                  ${(customerPerformance.totalPrivateRevenue + customerPerformance.totalRideshareRevenue).toFixed(2)}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Private: ${customerPerformance.totalPrivateRevenue.toFixed(2)}
                </p>
              </div>
              <DollarSign className="text-green-500" size={24} />
            </div>
          </div>
          
          <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Loyal Customers</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">
                  {customerPerformance.loyalCustomers}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  5+ rides
                </p>
              </div>
              <Heart className="text-purple-600" size={24} />
            </div>
          </div>
          
          <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Avg Customer Rating</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">
                  {customerPerformance.customers.length > 0 
                    ? (customerPerformance.customers.reduce((sum, c) => sum + (c.private_average_rating_received || 0), 0) / customerPerformance.customers.length).toFixed(1)
                    : '0.0'
                  }⭐
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Performance rating
                </p>
              </div>
              <Star className="text-yellow-500" size={24} />
            </div>
          </div>
          
          <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Total Trips</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">
                  {customerPerformance.totalPrivateTrips + customerPerformance.totalRideshareTrips}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Private: {customerPerformance.totalPrivateTrips} | Rideshare: {customerPerformance.totalRideshareTrips}
                </p>
              </div>
              <Car className="text-indigo-600" size={24} />
            </div>
          </div>
        </div>

        {/* Detailed Analytics Breakdown */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {/* Private vs Rideshare Breakdown */}
          <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
              <BarChart3 className="mr-2 text-blue-600" size={20} />
              Private vs Rideshare Analytics
            </h3>
            <div className="space-y-4">
              <div className="flex justify-between items-center p-3 bg-blue-50 dark:bg-blue-900/20 rounded">
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Private Trips</span>
                <span className="text-lg font-bold text-blue-600">{customerPerformance.totalPrivateTrips}</span>
              </div>
              <div className="flex justify-between items-center p-3 bg-green-50 dark:bg-green-900/20 rounded">
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Private Revenue</span>
                <span className="text-lg font-bold text-green-600">${customerPerformance.totalPrivateRevenue.toFixed(2)}</span>
              </div>
              <div className="flex justify-between items-center p-3 bg-purple-50 dark:bg-purple-900/20 rounded">
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Rideshare Trips</span>
                <span className="text-lg font-bold text-purple-600">{customerPerformance.totalRideshareTrips}</span>
              </div>
              <div className="flex justify-between items-center p-3 bg-orange-50 dark:bg-orange-900/20 rounded">
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Rideshare Revenue</span>
                <span className="text-lg font-bold text-orange-600">${customerPerformance.totalRideshareRevenue.toFixed(2)}</span>
              </div>
            </div>
          </div>

          {/* Customer Performance Metrics */}
          <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
              <Trophy className="mr-2 text-yellow-600" size={20} />
              Customer Performance Metrics
            </h3>
            <div className="space-y-4">
              <div className="flex justify-between items-center p-3 bg-yellow-50 dark:bg-yellow-900/20 rounded">
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">High Performers</span>
                <span className="text-lg font-bold text-yellow-600">{customerPerformance.highPerformers}</span>
              </div>
              <div className="flex justify-between items-center p-3 bg-indigo-50 dark:bg-indigo-900/20 rounded">
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Avg Completion Rate</span>
                <span className="text-lg font-bold text-indigo-600">{customerPerformance.avgCompletionRate.toFixed(1)}%</span>
              </div>
              <div className="flex justify-between items-center p-3 bg-pink-50 dark:bg-pink-900/20 rounded">
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Avg Customer Value</span>
                <span className="text-lg font-bold text-pink-600">${customerPerformance.avgCustomerValue.toFixed(2)}</span>
              </div>
              <div className="flex justify-between items-center p-3 bg-teal-50 dark:bg-teal-900/20 rounded">
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Avg Rating Received</span>
                <span className="text-lg font-bold text-teal-600">
                  {customerPerformance.customers.length > 0 
                    ? (customerPerformance.customers.reduce((sum, c) => sum + (c.private_average_rating_received || 0), 0) / customerPerformance.customers.filter(c => c.private_total_ratings_received > 0).length || 0).toFixed(1)
                    : '0.0'
                  }⭐
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Performance Distribution */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 mb-6">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
            <BarChart3 className="mr-2 text-blue-500" />
            Customer Performance Distribution
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { label: 'Excellent', min: 80, max: 100, color: 'green', customers: (customerPerformance.customers || []).filter(c => (c.totalSpent || 0) > 100) },
              { label: 'Good', min: 60, max: 79, color: 'blue', customers: (customerPerformance.customers || []).filter(c => (c.totalSpent || 0) > 50 && (c.totalSpent || 0) <= 100) },
              { label: 'Average', min: 40, max: 59, color: 'yellow', customers: (customerPerformance.customers || []).filter(c => (c.totalSpent || 0) > 20 && (c.totalSpent || 0) <= 50) },
              { label: 'Needs Improvement', min: 0, max: 39, color: 'red', customers: (customerPerformance.customers || []).filter(c => (c.totalSpent || 0) <= 20) }
            ].map(category => (
              <div key={category.label} className="border rounded-lg p-4 dark:border-gray-700">
                <div className="flex items-center justify-between mb-2">
                  <span className={`text-sm font-medium ${
                    category.color === 'green' ? 'text-green-600 dark:text-green-400' :
                    category.color === 'blue' ? 'text-blue-600 dark:text-blue-400' :
                    category.color === 'yellow' ? 'text-yellow-600 dark:text-yellow-400' :
                    'text-red-600 dark:text-red-400'
                  }`}>
                    {category.label}
                  </span>
                  <span className="text-sm text-gray-500 dark:text-gray-400">
                    {category.min}-{category.max}
                  </span>
                </div>
                <div className="text-2xl font-bold text-gray-900 dark:text-white mb-1">
                  {category.customers.length}
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-400">
                  {customerPerformance.customers && customerPerformance.customers.length > 0 ? 
                    ((category.customers.length / customerPerformance.customers.length) * 100).toFixed(1) : 0}% of total
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Private vs Rideshare Breakdown */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 mb-6">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
            <PieChart className="mr-2 text-blue-500" />
            Service Type Breakdown
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-lg font-semibold text-green-800 dark:text-green-200">Private Rides</h3>
                <Car className="text-green-600" size={24} />
              </div>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600 dark:text-gray-400">Total Trips:</span>
                  <span className="font-bold text-green-700 dark:text-green-300">{customerPerformance.totalPrivateTrips}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600 dark:text-gray-400">Total Revenue:</span>
                  <span className="font-bold text-green-700 dark:text-green-300">${customerPerformance.totalPrivateRevenue.toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600 dark:text-gray-400">Avg per Trip:</span>
                  <span className="font-bold text-green-700 dark:text-green-300">
                    ${customerPerformance.totalPrivateTrips > 0 ? (customerPerformance.totalPrivateRevenue / customerPerformance.totalPrivateTrips).toFixed(2) : '0.00'}
                  </span>
                </div>
              </div>
            </div>
            
            <div className="bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-lg font-semibold text-purple-800 dark:text-purple-200">Rideshare</h3>
                <Users className="text-purple-600" size={24} />
              </div>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600 dark:text-gray-400">Total Trips:</span>
                  <span className="font-bold text-purple-700 dark:text-purple-300">{customerPerformance.totalRideshareTrips}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600 dark:text-gray-400">Total Revenue:</span>
                  <span className="font-bold text-purple-700 dark:text-purple-300">${customerPerformance.totalRideshareRevenue.toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600 dark:text-gray-400">Avg per Trip:</span>
                  <span className="font-bold text-purple-700 dark:text-purple-300">
                    ${customerPerformance.totalRideshareTrips > 0 ? (customerPerformance.totalRideshareRevenue / customerPerformance.totalRideshareTrips).toFixed(2) : '0.00'}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Report Sections */}
        <div className="space-y-6">
          {/* Top Performers */}
          <div>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white flex items-center">
                <Award className="mr-2 text-blue-500" />
                Top Customer Performers
              </h2>
              <button
                onClick={() => toggleSection('topPerformers')}
                className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
              >
                {expandedSections.topPerformers ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
              </button>
            </div>
            
            {expandedSections.topPerformers && (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <CustomerTable
                  title="Top 20 Customers by Revenue"
                  data={topCustomersByRevenue}
                  icon={DollarSign}
                  metric="totalValue"
                  isHighest={true}
                />
                <CustomerTable
                  title="Top 20 Customers by Performance Score"
                  data={topCustomersByPerformance}
                  icon={Star}
                  metric="performanceScore"
                  isHighest={true}
                />
              </div>
            )}
          </div>

          {/* Customer Segments */}
          <div>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white flex items-center">
                <Users className="mr-2 text-green-500" />
                Customer Segments Analysis
              </h2>
              <button
                onClick={() => toggleSection('customerSegments')}
                className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
              >
                {expandedSections.customerSegments ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
              </button>
            </div>
            
            {expandedSections.customerSegments && (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <CustomerTable
                  title="Most Frequent Customers"
                  data={topCustomersByTrips}
                  icon={Clock}
                  metric="tripCount"
                  isHighest={true}
                />
                <CustomerTable
                  title="Inactive Customers"
                  data={inactiveCustomers.slice(0, 20)}
                  icon={Users}
                  metric="tripCount"
                  isHighest={false}
                />
              </div>
            )}
          </div>
        </div>

        {/* Report Tooltip */}
        <ReportTooltip title="Customer Performance Report - Business Value & Features">
          <div className="space-y-3">
            <div>
              <strong className="text-blue-900">👥 Report Overview:</strong>
              <p className="text-sm text-gray-700">Comprehensive analysis of customer performance metrics, revenue contribution, and engagement patterns with advanced scoring algorithm.</p>
            </div>
            <div>
              <strong className="text-green-900">💰 Business Value:</strong>
              <ul className="list-disc ml-4 space-y-1">
                <li>• Identify high-value customers for targeted marketing campaigns</li>
                <li>• Track customer loyalty and retention patterns effectively</li>
                <li>• Optimize customer acquisition and retention strategies</li>
                <li>• Improve customer experience through performance insights</li>
                <li>• Increase customer lifetime value (CLV) through data-driven decisions</li>
              </ul>
            </div>
            <div>
              <strong className="text-blue-900">✅ Key Features:</strong>
              <ul className="list-disc ml-4 space-y-1">
                <li>• <strong>Performance Scoring:</strong> 0-100 composite score based on trips, revenue, rating, consistency, and engagement</li>
                <li>• <strong>Smart Rankings:</strong> Top customers by performance score, revenue, trips, and ratings</li>
                <li>• <strong>Performance Distribution:</strong> Categories: Excellent (80-100), Good (60-79), Average (40-59), Needs Improvement (0-39)</li>
                <li>• <strong>Detailed Metrics:</strong> Completion rates, customer lifetime value, active days, and segmentation</li>
                <li>• <strong>Export Functionality:</strong> CSV download for all customer performance data</li>
              </ul>
            </div>
            
            <div>
              <strong className="text-blue-900">💼 Business Impact:</strong>
              <p>This report provides immediate management value by helping you identify top customers for rewards, recognize customers needing engagement, optimize marketing spend, improve customer satisfaction through targeted service, and maximize revenue through customer optimization.</p>
            </div>
            
            <div>
              <strong className="text-blue-900">🏆 Performance Score Algorithm:</strong>
              <p>Composite score calculation: Trip Volume (30%), Revenue Generation (25%), Customer Rating (20%), Trip Consistency (15%), Activity Consistency (10%). This weighted approach ensures balanced performance evaluation.</p>
            </div>
            
            <div>
              <strong className="text-blue-900">🔍 Data Source:</strong>
              <p>Uses REAL data from live API calls to trips, rideshare requests, customers, and drivers endpoints. All performance metrics are calculated from actual completed private trips and accepted rideshare requests, with real revenue and customer rating data.</p>
            </div>
          </div>
        </ReportTooltip>
      </div>
    </DashboardLayout>
  );
};

export default CustomerPerformanceReport;
